import { Confirm, Head, SaveView, sortOf, useAdmin, useFilters } from '../ui/Shell';
import { Alert, Block, Cell, DateRange, Grid, ListMore, Table, type Row, type Tab } from '../ui/Block';
import { fmtDate, post } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { countOf, qs, useDebounced, usePagedList, type Paged } from '../lib/list';
import { keyCols, keyRows, pl, stateCell, type ApiKey } from './Users';
import { KEY_STATES, customerName, openCustomer, type Customer } from './CustomerDrawer';
import { balanceCell } from './BillingAccounts';
import { subTone, type PlanRow } from './billingShared';
import { costCell, marginCell, revenueCell } from './costShared';

const PERIODS = [{ value: '', label: 'Money: all time' }, { value: '30', label: 'Money: 30 days' }, { value: '7', label: 'Money: 7 days' }];

const KINDS = [{ value: '', label: 'Any kind' }, { value: 'user', label: 'Users' }, { value: 'org', label: 'Organisations' }];
const STATES = [
  { value: '', label: 'Any state' }, { value: 'active', label: 'Active' }, { value: 'suspended', label: 'Suspended' }, { value: 'deleted', label: 'Deleted' },
  { value: 'purge_due', label: 'Purge due' }, { value: 'past_due', label: 'Past due' }, { value: 'debt', label: 'In debt' }, { value: 'zero', label: 'Balance ≤ 0' },
  { value: 'paid', label: 'Has paid' }, { value: 'unpaid', label: 'Never paid' }, { value: 'pending', label: 'Plan change pending' }, { value: 'subscribed', label: 'Subscribed' }, { value: 'retention', label: 'Data deletion pending' },
];
type TabKey = 'customers' | 'keys';

export function Customers() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [f, set] = useFilters({
    tab: 'customers', q: '', kind: '', plan: '', state: '', from: '', to: '', sort: '', dir: '', costDays: '', loss: '',
    kq: '', kstate: '', kfrom: '', kto: '', ksort: '', kdir: '',
  });
  const tab: TabKey = f.tab === 'keys' ? 'keys' : 'customers';
  const plans = useData<{ plans: PlanRow[] }>(env, '/admin/billing/plans', 60, paused);
  const planName = (id: string) => plans.data?.plans.find((x) => x.plan.id === id)?.plan.name ?? id;

  const q = useDebounced(f.q.trim(), 300);
  const cs = sortOf(f, set, { key: 'active', dir: 'desc' });
  const cPath = tab === 'customers' ? `/admin/customers${qs({ q, kind: f.kind, plan: f.plan, state: f.state, from: f.from, to: f.to, costDays: f.costDays, loss: f.loss, sort: f.sort, dir: f.sort ? cs.sort.dir : '' })}` : null;
  const cl = usePagedList<Customer, Paged & { customers: Customer[]; purgeDue: number }>(env, cPath, 'customers', 30, paused, (c) => c.principalId);

  const kq = useDebounced(f.kq.trim(), 300);
  const ks = sortOf({ sort: f.ksort, dir: f.kdir }, (p) => set({ ksort: p.sort, kdir: p.dir }), { key: 'created', dir: 'desc' });
  const kPath = tab === 'keys' ? `/admin/api-keys${qs({ q: kq, state: f.kstate, from: f.kfrom, to: f.kto, sort: f.ksort, dir: f.ksort ? ks.sort.dir : '' })}` : null;
  const kl = usePagedList<ApiKey, Paged & { keys: ApiKey[] }>(env, kPath, 'keys', 30, paused);

  const due = cl.data?.purgeDue ?? ctx.attention?.counts.purgeDue ?? 0;
  const purgeAllDue = () => ctx.openModal(
    <Confirm env={env} app={`${pl(due, 'account')} past the restore window`} mobile={mobile} danger deskOnly title="Purge due accounts" confirmLabel={`Purge ${pl(due, 'account')}`} onClose={ctx.closeModal}
      body="Purges every account the customer deleted more than 30 days ago, as Purge account does: content, voices and files go for good; the billing records are kept under Purged accounts for 7 years. No email is sent."
      onConfirm={async () => {
        const r = await post<{ purged: number }>(env, '/admin/users/purge-due');
        ctx.toast(`Purged ${pl(r.purged, 'account')}`, 'IdP deletions queued');
        if (f.state === 'purge_due') set({ state: '' });
        cl.reload();
        ctx.reloadAttention();
      }} />,
  );

  const tabs: Tab[] = [{ key: 'customers', label: 'Customers', count: tab === 'customers' ? countOf(cl) : undefined }, { key: 'keys', label: 'API keys', count: tab === 'keys' ? countOf(kl) : undefined }];
  const onTab = (k: string) => set({ tab: k });

  const cRows: Row[] = cl.rows.map((c) => {
    const b = c.billing;
    return {
      key: c.principalId,
      onClick: () => openCustomer(ctx, c.principalId),
      bg: c.suspendedAt || c.deletedAt ? 'var(--conf-low-soft)' : undefined,
      cells: [
        { t: customerName(c), fw: 600, sub: c.kind === 'org' ? pl(c.members ?? 0, 'member') : c.displayName || 'user' },
        { pill: { label: c.kind === 'org' ? 'ORG' : 'USER', tone: c.kind === 'org' ? 'info' : 'mute' } },
        b?.planId ? { t: planName(b.planId), sub: b.pendingChange ? 'change pending' : undefined } : { t: '—', fg: 'var(--ink-40)' },
        b ? balanceCell(b.balance) : { t: '—', fg: 'var(--ink-40)' },
        b?.subscriptionStatus ? { pill: { label: b.subscriptionStatus.toUpperCase(), tone: subTone(b.subscriptionStatus) } } : { t: '—', fg: 'var(--ink-40)' },
        revenueCell(c.costs), costCell(c.costs), marginCell(c.costs),
        { t: fmtDate(c.createdAt) },
        { t: c.lastActiveAt ? fmtDate(c.lastActiveAt) : 'never', fg: c.lastActiveAt ? undefined : 'var(--ink-45)' },
        stateCell(c),
      ],
    };
  });
  const filtered = !!(q || f.kind || f.plan || f.state || f.from || f.to || f.loss);
  const kFiltered = !!(kq || f.kstate || f.kfrom || f.kto);
  const cErr = describeErr(cl.error);
  const kErr = describeErr(kl.error);

  return (
    <>
      <Head title="Customers" sub="Every account, a user's own or an organisation's, with its billing and what it earned and cost us (profit is what we keep after Polar's fee, less every cost; sort by it or pick Losing money to find accounts that cost more than they pay); and every API key. Search matches email, name, ids, WorkOS id, key prefix and organisation members." />
      <Grid>
        {due > 0 && (
          <Cell span={12}>
            <Alert severity="action" text={`${pl(due, 'deleted account')} past the 30-day restore window`}
              sub="The Privacy Policy and Terms promise these are permanently deleted after 30 days. Nothing purges them automatically."
              acts={[
                f.state === 'purge_due' && tab === 'customers' ? { label: 'Show all', onClick: () => set({ state: '' }) } : { label: 'Show them', onClick: () => set({ tab: 'customers', state: 'purge_due' }) },
                ...(mobile ? [] : [{ label: 'Purge due accounts', danger: true, onClick: purgeAllDue }]),
              ]} />
          </Cell>
        )}
        <Cell span={12}>
          {tab === 'customers' ? (
            <Block title="Customers" count={countOf(cl)} asOf={hhmmss(cl.at)} stale={cl.ageS > 60} loading={cl.loading && !cl.data}
              error={cErr ? { ...cErr, retry: cl.reload } : null} tabs={tabs} tab={tab} onTab={onTab}
              search={{ value: f.q, placeholder: 'Email, name, id, WorkOS id or key prefix', onChange: (v) => set({ q: v }) }}
              selects={[
                { value: f.kind, options: KINDS, onChange: (v) => set({ kind: v }), title: 'Kind' },
                { value: f.plan, options: [{ value: '', label: 'Any plan' }, ...(plans.data?.plans ?? []).map((x) => ({ value: x.plan.id, label: x.plan.name }))], onChange: (v) => set({ plan: v }), title: 'Plan' },
                { value: f.state, options: STATES, onChange: (v) => set({ state: v }), title: 'State' },
                { value: f.costDays, options: PERIODS, onChange: (v) => set({ costDays: v }), title: 'The period Revenue, Cost and Profit sum over' },
              ]}
              chips={[{ label: 'Losing money', on: f.loss === 'true', onClick: () => set({ loss: f.loss === 'true' ? '' : 'true' }) }]}
              extra={<><DateRange label="Created" from={f.from} to={f.to} onChange={(from, to) => set({ from, to })} /><SaveView /></>}
              empty={cl.data && !cl.rows.length ? (filtered ? 'No customer matches these filters.' : 'No customers yet.') : undefined}
              foot={<ListMore list={cl} noun="customers" />}>
              <Table mobile={mobile} minWidth={1560} rows={cRows} sort={cs.sort} onSort={cs.onSort}
                cols={[
                  { label: 'CUSTOMER', width: 'minmax(220px,1.8fr)', sort: 'name', sortAsc: true }, { label: 'KIND', width: '70px', sort: 'kind', sortAsc: true }, { label: 'PLAN', width: 'minmax(110px,1fr)', sort: 'plan', sortAsc: true },
                  { label: 'BALANCE', width: '100px', align: 'r', sort: 'balance', sortAsc: true }, { label: 'SUBSCRIPTION', width: '110px', sort: 'subscription', sortAsc: true },
                  { label: 'REVENUE', width: 'minmax(96px,.8fr)', align: 'r', sort: 'revenue' }, { label: 'COST', width: 'minmax(104px,.8fr)', align: 'r', sort: 'cost' }, { label: 'PROFIT / LOSS', width: 'minmax(124px,.9fr)', align: 'r', sort: 'profit', sortAsc: true },
                  { label: 'CREATED', width: '120px', sort: 'created' }, { label: 'LAST ACTIVE', width: '120px', sort: 'active' }, { label: 'STATE', width: 'minmax(130px,auto)', sort: 'state', sortAsc: true },
                ]} />
            </Block>
          ) : (
            <Block title="API keys" count={countOf(kl)} asOf={hhmmss(kl.at)} stale={kl.ageS > 60} loading={kl.loading && !kl.data}
              error={kErr ? { ...kErr, retry: kl.reload } : null} tabs={tabs} tab={tab} onTab={onTab}
              search={{ value: f.kq, placeholder: 'Key prefix, name, owner email or organisation', onChange: (v) => set({ kq: v }) }}
              selects={[{ value: f.kstate, options: KEY_STATES, onChange: (v) => set({ kstate: v }), title: 'State' }]}
              extra={<><DateRange label="Created" from={f.kfrom} to={f.kto} onChange={(from, to) => set({ kfrom: from, kto: to })} /><SaveView /></>}
              empty={kl.data && !kl.rows.length ? (kFiltered ? 'No key matches these filters.' : 'No API keys.') : undefined}
              foot={<ListMore list={kl} noun="keys" />}>
              <Table mobile={mobile} minWidth={1180} sort={ks.sort} onSort={ks.onSort} cols={keyCols(true)}
                rows={keyRows(ctx, kl.rows, kl.reload, true, (k) => openCustomer(ctx, k.principalId, 'keys'))} />
            </Block>
          )}
        </Cell>
      </Grid>
    </>
  );
}
